// @ts-nocheck
/* ============================================================
   Fence Works — building-footprint (house) detection.

   The fence ties into the house ("halfway up the house") and the
   backyard is laid out relative to it, so we need the house outline
   on the lot. Two sources, best-effort and graceful:

     1) OpenStreetMap (Overpass)  — real, mapped building polygons.
        Accurate & instant where present (most established areas).
     2) CV fallback               — when OSM has nothing (newer/rural
        lots), detect the largest "built" blob near the parcel centre
        in an Esri aerial export and return its footprint as a
        rectangle. Approximate; the contractor drags it to fit.

   Plus pure geometry the estimator reuses: frontRearEdges() and
   backyardFence() (a rear-yard enclosure that ties into the house).

   Exposed as window.VFP_BUILDINGS — same IIFE/self-register pattern
   as lib/parcel.ts and lib/slope.ts.
   ============================================================ */
(function () {
  "use strict";
  const M_TO_FT = 3.28084;

  function fetchT(url, opts, ms) {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), ms || 9000);
    return fetch(url, Object.assign({ signal: ctrl.signal }, opts || {})).finally(() => clearTimeout(t));
  }

  /* ---- lat/lng geometry helpers (local-planar in meters via a shared origin) ---- */
  function bboxOf(poly) {
    let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
    poly.forEach(([la, ln]) => { if (la < minLat) minLat = la; if (la > maxLat) maxLat = la; if (ln < minLng) minLng = ln; if (ln > maxLng) maxLng = ln; });
    return { minLat, maxLat, minLng, maxLng };
  }
  function padBbox(b, frac) {
    const dLat = (b.maxLat - b.minLat) * frac, dLng = (b.maxLng - b.minLng) * frac;
    return { minLat: b.minLat - dLat, maxLat: b.maxLat + dLat, minLng: b.minLng - dLng, maxLng: b.maxLng + dLng };
  }
  function centroidLL(poly) { const n = poly.length; let la = 0, ln = 0; poly.forEach(p => { la += p[0]; ln += p[1]; }); return [la / n, ln / n]; }
  const toXY = (p, o) => { const k = Math.cos(o[0] * Math.PI / 180); return [(p[1] - o[1]) * k * 111320, (p[0] - o[0]) * 110540]; };
  const toLL = (xy, o) => { const k = Math.cos(o[0] * Math.PI / 180); return [o[0] + xy[1] / 110540, o[1] + xy[0] / (k * 111320)]; };
  function areaFt2(poly) {
    const o = centroidLL(poly); let a = 0;
    for (let i = 0; i < poly.length; i++) { const A = toXY(poly[i], o), B = toXY(poly[(i + 1) % poly.length], o); a += A[0] * B[1] - B[0] * A[1]; }
    return Math.abs(a / 2) * M_TO_FT * M_TO_FT;
  }
  // ray-cast point-in-polygon; P and ring points are [lat,lng] (lat=y, lng=x)
  function pointInPoly(P, poly) {
    let c = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i][1], yi = poly[i][0], xj = poly[j][1], yj = poly[j][0];
      if (((yi > P[0]) !== (yj > P[0])) && (P[1] < (xj - xi) * (P[0] - yi) / (yj - yi) + xi)) c = !c;
    }
    return c;
  }

  /* ---- 1) OpenStreetMap buildings in a bbox (Overpass) ---- */
  async function osmBuildings(bb) {
    const box = `${bb.minLat},${bb.minLng},${bb.maxLat},${bb.maxLng}`;
    const q = `[out:json][timeout:25];(way["building"](${box});relation["building"](${box}););out geom;`;
    const r = await fetchT("https://overpass-api.de/api/interpreter", {
      method: "POST", body: "data=" + encodeURIComponent(q),
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    }, 15000);
    if (!r.ok) throw new Error("overpass " + r.status);
    const j = await r.json();
    const polys = [];
    (j.elements || []).forEach((el) => {
      if (el.type === "way" && Array.isArray(el.geometry)) {
        const ring = el.geometry.map((g) => [g.lat, g.lon]);
        if (ring.length >= 4) polys.push(dedupeRing(ring));
      } else if (el.type === "relation" && Array.isArray(el.members)) {
        el.members.filter((m) => m.role === "outer" && Array.isArray(m.geometry)).forEach((m) => {
          const ring = m.geometry.map((g) => [g.lat, g.lon]);
          if (ring.length >= 4) polys.push(dedupeRing(ring));
        });
      }
    });
    return polys;
  }
  // OSM rings repeat the first point as the last; drop it so our rings are open-ended
  function dedupeRing(ring) {
    if (ring.length > 1) { const a = ring[0], b = ring[ring.length - 1]; if (Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9) return ring.slice(0, -1); }
    return ring;
  }

  /* ---- 2) FEMA / Oak Ridge "USA Structures": real footprints for every US
     structure > 450 sq ft (free, public ArcGIS). The authoritative source when
     OSM hasn't mapped the building (rural lots / new subdivisions) — far more
     reliable than reading pixels in the browser. ---- */
  async function femaStructures(bb) {
    const q = "https://services2.arcgis.com/FiaPA4ga0iQKduv3/arcgis/rest/services/USA_Structures_View/FeatureServer/0/query?"
      + "geometry=" + encodeURIComponent(`${bb.minLng},${bb.minLat},${bb.maxLng},${bb.maxLat}`)
      + "&geometryType=esriGeometryEnvelope&inSR=4326&spatialRel=esriSpatialRelIntersects&outFields=&returnGeometry=true&outSR=4326&f=geojson";
    const r = await fetchT(q, {}, 12000);
    if (!r.ok) throw new Error("usa-structures " + r.status);
    const j = await r.json();
    const polys = [];
    (j.features || []).forEach((f) => {
      const g = f.geometry; if (!g) return;
      if (g.type === "Polygon") { const ring = g.coordinates[0].map(([x, y]) => [y, x]); if (ring.length >= 4) polys.push(dedupeRing(ring)); }
      else if (g.type === "MultiPolygon") g.coordinates.forEach((poly) => { const ring = poly[0].map(([x, y]) => [y, x]); if (ring.length >= 4) polys.push(dedupeRing(ring)); });
    });
    return polys;
  }

  /* ---- 3) CV fallback: largest built blob near the parcel centre in an aerial ---- */
  function loadImg(url) {
    return new Promise((res, rej) => { const im = new Image(); im.crossOrigin = "anonymous"; im.onload = () => res(im); im.onerror = () => rej(new Error("img")); im.src = url; });
  }
  async function cvBuildings(bb, parcelPolygon) {
    const W = 512, H = 512;
    // Esri World Imagery "export": one georeferenced image for the bbox, CORS-enabled
    // (the live tile layer isn't crossOrigin, so we fetch a separate readable image).
    const url = `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/export?bbox=${bb.minLng},${bb.minLat},${bb.maxLng},${bb.maxLat}&bboxSR=4326&imageSR=4326&size=${W},${H}&format=png&transparent=false&f=image`;
    const img = await loadImg(url);
    const cv = document.createElement("canvas"); cv.width = W; cv.height = H;
    const ctx = cv.getContext("2d"); ctx.drawImage(img, 0, 0, W, H);
    let data;
    try { data = ctx.getImageData(0, 0, W, H).data; } catch (e) { return []; }   // CORS taint → bail to "no house"
    // bbox is geographic, so pixels map linearly; row 0 = maxLat (north)
    const px2ll = (x, y) => [bb.maxLat - (y / H) * (bb.maxLat - bb.minLat), bb.minLng + (x / W) * (bb.maxLng - bb.minLng)];
    // restrict detection to INSIDE the selected lot — on a big parcel the bbox is
    // mostly neighbours / road, so without this CV grabs the wrong building.
    const parcelPx = parcelPolygon.map(([la, ln]) => [((ln - bb.minLng) / (bb.maxLng - bb.minLng)) * W, ((bb.maxLat - la) / (bb.maxLat - bb.minLat)) * H]);
    const inParcel = (x, y) => { let c = false; for (let i = 0, j = parcelPx.length - 1; i < parcelPx.length; j = i++) { const xi = parcelPx[i][0], yi = parcelPx[i][1], xj = parcelPx[j][0], yj = parcelPx[j][1]; if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) c = !c; } return c; };
    // mask "built" surfaces INSIDE the lot: grayish (low saturation), not green, not deep shadow
    const mask = new Uint8Array(W * H);
    let lotAreaPx = 0;
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      if (!inParcel(x, y)) continue;
      lotAreaPx++;
      const i = y * W + x, r = data[i * 4], g = data[i * 4 + 1], b = data[i * 4 + 2];
      const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
      const sat = mx === 0 ? 0 : (mx - mn) / mx;
      const greenish = g > r + 8 && g > b + 8;
      mask[i] = (!greenish && sat < 0.22 && mx > 30) ? 1 : 0;
    }
    if (lotAreaPx < 400) return [];   // lot barely covers the frame
    // parcel centroid in pixels (centrality bias)
    const pc = centroidLL(parcelPolygon);
    const pcx = ((pc[1] - bb.minLng) / (bb.maxLng - bb.minLng)) * W;
    const pcy = ((bb.maxLat - pc[0]) / (bb.maxLat - bb.minLat)) * H;
    const diag = Math.hypot(W, H);
    // connected components (4-neighbour, iterative)
    const lab = new Int32Array(W * H).fill(0);
    let best = null;
    const stack = [];
    for (let s = 0; s < W * H; s++) {
      if (!mask[s] || lab[s]) continue;
      let count = 0, sx = 0, sy = 0, minX = W, minY = H, maxX = 0, maxY = 0, border = 0;
      stack.length = 0; stack.push(s); lab[s] = 1;
      while (stack.length) {
        const p = stack.pop(); const x = p % W, y = (p / W) | 0;
        count++; sx += x; sy += y;
        if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y;
        if (x === 0 || y === 0 || x === W - 1 || y === H - 1) border++;
        if (x > 0 && mask[p - 1] && !lab[p - 1]) { lab[p - 1] = 1; stack.push(p - 1); }
        if (x < W - 1 && mask[p + 1] && !lab[p + 1]) { lab[p + 1] = 1; stack.push(p + 1); }
        if (y > 0 && mask[p - W] && !lab[p - W]) { lab[p - W] = 1; stack.push(p - W); }
        if (y < H - 1 && mask[p + W] && !lab[p + W]) { lab[p + W] = 1; stack.push(p + W); }
      }
      const bw = maxX - minX + 1, bh = maxY - minY + 1;
      const compact = count / (bw * bh);
      const frac = count / lotAreaPx;                        // relative to the LOT, so a small house on a big parcel still counts
      if (count < 80 || frac > 0.85) continue;               // noise, or basically the whole lot
      if (compact < 0.35) continue;                          // sparse/elongated (driveways, treelines)
      const cx = sx / count, cy = sy / count;
      const central = 1 - Math.min(1, Math.hypot(cx - pcx, cy - pcy) / (diag * 0.5));
      const score = count * compact * (0.4 + 0.6 * central);
      if (!best || score > best.score) best = { score, minX, minY, maxX, maxY };
    }
    if (!best) return [];
    // return the blob's bounding box as a 4-corner footprint (approximate)
    const rect = [[best.minX, best.minY], [best.maxX, best.minY], [best.maxX, best.maxY], [best.minX, best.maxY]];
    return [rect.map(([x, y]) => px2ll(x, y))];
  }

  /* ---- detection: OSM first, CV fallback ---- */
  const cache = new Map();
  async function detectHouse(parcelPolygon) {
    if (!Array.isArray(parcelPolygon) || parcelPolygon.length < 3) return null;
    const c = centroidLL(parcelPolygon);
    const key = c[0].toFixed(5) + "," + c[1].toFixed(5);
    if (cache.has(key)) return cache.get(key);
    const bb = padBbox(bboxOf(parcelPolygon), 0.10);
    // keep only buildings on THIS lot (centroid inside, else any vertex inside)
    const mineOf = (cands) => {
      let m = (cands || []).filter((p) => pointInPoly(centroidLL(p), parcelPolygon));
      if (!m.length) m = (cands || []).filter((p) => p.some((v) => pointInPoly(v, parcelPolygon)));
      return m;
    };
    let source = "osm", mine = [];
    try { mine = mineOf(await osmBuildings(bb)); } catch (e) { mine = []; }
    if (!mine.length) {   // OSM blank → national footprint dataset (authoritative)
      try { mine = mineOf(await femaStructures(bb)); if (mine.length) source = "usa"; } catch (e) { /* fall through */ }
    }
    if (!mine.length) {   // last resort → pixel CV from the aerial
      try { mine = await cvBuildings(bb, parcelPolygon); if (mine.length) source = "cv"; } catch (e) { mine = []; }
    }
    if (!mine.length) { cache.set(key, null); return null; }
    mine.sort((a, b) => areaFt2(b) - areaFt2(a));          // biggest footprint on the lot = the house
    const house = mine[0];
    const out = { polygon: house, source, areaFt2: Math.round(areaFt2(house)), all: mine };
    cache.set(key, out);
    return out;
  }

  /* ---- pure layout geometry the estimator reuses ---- */
  const distPtSeg = (P, A, B) => {
    const dx = B[0] - A[0], dy = B[1] - A[1], l2 = dx * dx + dy * dy;
    let t = l2 < 1e-9 ? 0 : ((P[0] - A[0]) * dx + (P[1] - A[1]) * dy) / l2; t = t < 0 ? 0 : t > 1 ? 1 : t;
    return Math.hypot(P[0] - (A[0] + t * dx), P[1] - (A[1] + t * dy));
  };
  // front lot edge = nearest the house; rear = farthest (the house sits toward the street)
  function frontRearEdges(lotPolygon, housePolygon) {
    if (!lotPolygon || lotPolygon.length < 3 || !housePolygon || housePolygon.length < 3) return null;
    const o = centroidLL(lotPolygon);
    const P = lotPolygon.map((p) => toXY(p, o));
    const Hc = centroidLL(housePolygon); const Hcxy = toXY(Hc, o);
    let front = 0, fd = Infinity, rear = 0, rd = -1;
    for (let i = 0; i < P.length; i++) { const d = distPtSeg(Hcxy, P[i], P[(i + 1) % P.length]); if (d < fd) { fd = d; front = i; } if (d > rd) { rd = d; rear = i; } }
    return { front, rear };
  }
  function clipHalfPlane(poly, Q, nrm) {   // keep the side where (p-Q)·nrm >= 0
    const out = [], n = poly.length, side = (p) => (p[0] - Q[0]) * nrm[0] + (p[1] - Q[1]) * nrm[1];
    for (let i = 0; i < n; i++) {
      const A = poly[i], B = poly[(i + 1) % n], sa = side(A), sb = side(B);
      if (sa >= 0) out.push(A);
      if ((sa < 0) !== (sb < 0)) { const t = sa / (sa - sb); out.push([A[0] + t * (B[0] - A[0]), A[1] + t * (B[1] - A[1])]); }
    }
    return out;
  }
  // a rear-yard fence that ties into the house at `depthFrac` of its depth ("halfway
  // up the house" = 0.5). Returns an OPEN polyline [lat,lng] (the house wall closes
  // the loop) or null if the lot/house don't yield a sensible cut.
  function backyardFence(lotPolygon, housePolygon, depthFrac) {
    if (!lotPolygon || lotPolygon.length < 3 || !housePolygon || housePolygon.length < 3) return null;
    const frac = depthFrac == null ? 0.5 : Math.max(0.05, Math.min(0.95, depthFrac));
    const o = centroidLL(lotPolygon);
    const P = lotPolygon.map((p) => toXY(p, o));
    const H = housePolygon.map((p) => toXY(p, o));
    // Backyard direction d: from the house toward the OPEN part of the lot. The
    // lot's XY centroid is ~the origin, so that's just "away from the house's
    // offset" — far more reliable on irregular cul-de-sac lots than guessing the
    // front edge. The cut runs perpendicular to d, so it ties into the house's
    // SIDE walls and the fence comes off the sides (the typical backyard layout).
    // Falls back to the front→rear lot-edge axis only if the house sits dead-centre.
    const Hc = H.reduce((a, h) => [a[0] + h[0], a[1] + h[1]], [0, 0]).map((v) => v / H.length);
    const hcLen = Math.hypot(Hc[0], Hc[1]);
    let d;
    if (hcLen > 8) d = [-Hc[0] / hcLen, -Hc[1] / hcLen];
    else {
      const fr = frontRearEdges(lotPolygon, housePolygon); if (!fr) return null;
      const fA = P[fr.front], fB = P[(fr.front + 1) % P.length], rA = P[fr.rear], rB = P[(fr.rear + 1) % P.length];
      const fM = [(fA[0] + fB[0]) / 2, (fA[1] + fB[1]) / 2], rM = [(rA[0] + rB[0]) / 2, (rA[1] + rB[1]) / 2];
      const dx = rM[0] - fM[0], dy = rM[1] - fM[1], dl = Math.hypot(dx, dy) || 1; d = [dx / dl, dy / dl];
    }
    const s = [-d[1], d[0]];                                          // across the lot
    // tie depth: project the house onto d, cut at `frac` of its depth (0.5 = halfway
    // up the house). Measured from the origin so Q lands on the cut line.
    const hp = H.map((h) => h[0] * d[0] + h[1] * d[1]);
    const hpMin = Math.min(...hp), hpMax = Math.max(...hp);
    const cutProj = hpMin + frac * (hpMax - hpMin);
    const Q = [d[0] * cutProj, d[1] * cutProj];
    // keep the lot behind the cut line (toward the rear)
    const R = clipHalfPlane(P, Q, d);
    if (R.length < 3) return null;
    // the two clip-created vertices sit on the cut line (|(p-Q)·d| ≈ 0); they're the
    // points where the side lot lines meet the tie depth.
    const onCut = []; for (let i = 0; i < R.length; i++) { if (Math.abs((R[i][0] - Q[0]) * d[0] + (R[i][1] - Q[1]) * d[1]) < 0.05) onCut.push(i); }
    if (onCut.length < 2) return null;
    const i0 = onCut[0], i1 = onCut[onCut.length - 1];
    // house ∩ cut line → tie points on the house walls (sorted along s)
    const hits = [];
    for (let i = 0; i < H.length; i++) {
      const A = H[i], B = H[(i + 1) % H.length], sa = (A[0] - Q[0]) * d[0] + (A[1] - Q[1]) * d[1], sb = (B[0] - Q[0]) * d[0] + (B[1] - Q[1]) * d[1];
      if ((sa < 0) !== (sb < 0)) { const t = sa / (sa - sb); hits.push([A[0] + t * (B[0] - A[0]), A[1] + t * (B[1] - A[1])]); }
    }
    if (hits.length < 2) return null;
    const sOf = (p) => (p[0] - Q[0]) * s[0] + (p[1] - Q[1]) * s[1];
    hits.sort((a, b) => sOf(a) - sOf(b));
    const HL = hits[0], HR = hits[hits.length - 1];
    // R has two arcs between the cut vertices: the straight CUT EDGE (short) and
    // the real lot boundary around the back (long). Walk both and keep the longer
    // one — picking the wrong (short) arc is what produced the degenerate stub.
    const arcFrom = (a, b) => { const out = []; for (let k = 0; k <= R.length; k++) { const idx = (a + k) % R.length; out.push(R[idx]); if (idx === b) break; } return out; };
    const plen = (pts) => { let L = 0; for (let k = 0; k < pts.length - 1; k++) L += Math.hypot(pts[k + 1][0] - pts[k][0], pts[k + 1][1] - pts[k][1]); return L; };
    const a1 = arcFrom(i0, i1), a2 = arcFrom(i1, i0);
    const arc = plen(a1) >= plen(a2) ? a1 : a2;
    // tie HL/HR to the cut vertex on its own side of the lot
    const startTie = sOf(arc[0]) <= sOf(arc[arc.length - 1]) ? HL : HR;
    const endTie = startTie === HL ? HR : HL;
    const fenceXY = [startTie, ...arc, endTie];
    // guard: reject a degenerate sliver so Backyard never lays down a stub line
    if (fenceXY.length < 4 || plen(fenceXY) < 12) return null;   // ~40 ft minimum
    return fenceXY.map((p) => toLL(p, o));
  }

  window.VFP_BUILDINGS = { detectHouse, frontRearEdges, backyardFence, _cache: cache };
})();
